import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';
import { ToastContainerComponent } from './toast-container/toast-container.component';


/** Event that is emitted when a toast is dismissed. */
export interface ToastDismiss {
    /** Whether the toast was dismissed using the action button. */
    dismissedByAction: boolean;
}

/** Maximum amount of milliseconds that can be passed into setTimeout. */
const MAX_TIMEOUT = Math.pow(2, 31) - 1;

/**
 * Reference to a toast dispatched from the toast service.
 */
export class ToastRef<T> {
    /** The instance of the component making up the content of the toast. */
    instance: T;

    /**
     * The instance of the component making up the content of the toast.
     * @docs-private
     */
    containerInstance: ToastContainerComponent;

    /** Subject for notifying the user that the toast has been dismissed. */
    private readonly afterDismissedRef = new Subject<ToastDismiss>();

    /** Subject for notifying the user that the toast has opened and appeared. */
    private readonly afterOpenedRef = new Subject<void>();

    /** Subject for notifying the user that the toast action was called. */
    private readonly onActionRef = new Subject<void>();

    /**
     * Timeout ID for the duration setTimeout call. Used to clear the timeout if the snackbar is
     * dismissed before the duration passes.
     */
    private durationTimeoutId: number;

    /** Whether the toast was dismissed using the action button. */
    private dismissedByAction = false;

    constructor(
        containerInstance: ToastContainerComponent,
        private overlayRef: OverlayRef) {
        this.containerInstance = containerInstance;
        // Dismiss snackbar on action.
        this.onAction().subscribe(() => this.dismiss());
        containerInstance.onExit.subscribe(() => this.finishDismiss());
    }

    /** Dismisses the toast. */
    dismiss(): void {
        if (!this.afterDismissedRef.closed) {
            this.containerInstance.exit();
        }
        clearTimeout(this.durationTimeoutId);
    }

    /** Marks the snackbar action clicked. */
    dismissWithAction(): void {
        if (!this.onActionRef.closed) {
            this.dismissedByAction = true;
            this.onActionRef.next();
            this.onActionRef.complete();
        }
    }

    /**
     * Marks the snackbar action clicked.
     * @deprecated Use `dismissWithAction` instead.
     * @breaking-change 8.0.0
     */
    closeWithAction(): void {
        this.dismissWithAction();
    }

    /** Dismisses the toast after some duration */
    dismissAfter(duration: number): void {
        // Note that we need to cap the duration to the maximum value for setTimeout, because
        // it'll revert to 1 if somebody passes in something greater (e.g. `Infinity`). See #17234.
        this.durationTimeoutId = setTimeout(() => this.dismiss(), Math.min(duration, MAX_TIMEOUT)) as unknown as number;
    }

    /** Marks the snackbar as opened */
    open(): void {
        if (!this.afterOpenedRef.closed) {
            this.afterOpenedRef.next();
            this.afterOpenedRef.complete();
        }
    }

    /** Cleans up the DOM after closing. */
    private finishDismiss(): void {
        this.overlayRef.dispose();

        if (!this.onActionRef.closed) {
            this.onActionRef.complete();
        }

        this.afterDismissedRef.next({ dismissedByAction: this.dismissedByAction });
        this.afterDismissedRef.complete();
        this.dismissedByAction = false;
    }

    /** Gets an observable that is notified when the toast is finished closing. */
    afterDismissed(): Observable<ToastDismiss> {
        return this.afterDismissedRef.asObservable();
    }

    /** Gets an observable that is notified when the toast has opened and appeared. */
    afterOpened(): Observable<void> {
        return this.containerInstance.onEnter;
    }

    /** Gets an observable that is notified when the toast action is called. */
    onAction(): Observable<void> {
        return this.onActionRef.asObservable();
    }
}
